import React, { Ref, useImperativeHandle, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { cn } from '@/lib/utils/cn';
import { useStanDraggableContext } from './StanDraggable.context';
import styles from './StanDraggable.module.css';

export interface DraggableWrapperProps {
    id: string;
    children: React.ReactNode;
    disabled?: boolean;
    className?: string;
    dragFromHandleOnly?: boolean;
    data?: Record<string, any>;
    ref?: Ref<HTMLDivElement>;
}

export const DraggableWrapper = ({
    id,
    children,
    disabled = false,
    className = '',
    dragFromHandleOnly = false,
    ref,
}: DraggableWrapperProps) => {
    // Try to get context, but don't require it
    let contextDisabled = false;
    let contextDragFromHandleOnly = false;

    try {
        const context = useStanDraggableContext();
        contextDisabled = context.disabled || false;
        contextDragFromHandleOnly = context.dragFromHandleOnly || false;
    } catch {
        // Context not available, use defaults
    }

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging: isSortableDragging,
    } = useSortable({
        id,
        disabled: contextDisabled || disabled,
    });

    const finalDragFromHandleOnly = dragFromHandleOnly ?? contextDragFromHandleOnly ?? false;

    const internalRef = useRef<HTMLDivElement | null>(null);

    useImperativeHandle(ref, () => internalRef.current as HTMLDivElement);

    const setRef = (node: HTMLDivElement | null) => {
        internalRef.current = node;
        setNodeRef(node);
    };

    const style = {
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition: isSortableDragging ? 'none' : transition,
    };

    const itemClasses = cn(
        styles.item,
        {
            [styles.disabled as string]: contextDisabled || disabled,
            [styles.handleOnly as string]: finalDragFromHandleOnly,
        },
        className
    );

    return (
        <div
            ref={setRef}
            className={itemClasses}
            style={style}
            {...attributes}
            {...(finalDragFromHandleOnly ? {} : listeners)}
        >
            {children}
        </div>
    );
};

DraggableWrapper.displayName = 'DraggableWrapper';
